import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { useContextMenu } from 'react-contexify';
import Form from 'react-bootstrap/Form';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { Editor } from '@tinymce/tinymce-react';
import isEmpty from 'lodash/isEmpty';
import { FaCopy, FaPlus, FaSave, FaTrash } from 'react-icons/fa';

import { FormTextField, Button, PageHeader } from '_components/_core';
import { isFeatureEnabled, useMediaQuery } from 'helpers';
import { ValidationSchema, TEMPLATES, VARIABLES } from './utilities';
import VariablesList from './VariablesList/VariablesList';
import {
  extractCustomVariables,
  extractCustomVariablesWithTypes,
} from '_components/Financial/ContractGeneration/utilities';

import { Toolbar, ToolbarItem } from './styles';

const EDITOR_SCRIPT_PATH = `${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`;
const EDITOR_TRANSLATION_PATH = `${process.env.PUBLIC_URL}/assets/langs/pt_BR.js`;

function Contracts({
  onFetchAllContracts,
  activeCompany,
  onAddContract,
  onUpdateContract,
  onDeleteContract,
  contracts,
  user,
}) {
  const { isMobile } = useMediaQuery();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState(null);
  const [customVariables, setCustomVariables] = useState([]);

  const editorRef = useRef(null);
  const MENU_ID = 'contractMenu';

  const { show, hideAll } = useContextMenu({
    id: MENU_ID,
  });

  const selectedContract = useMemo(
    () => contracts.find(contract => String(contract._id) === selectedContractId),
    [contracts, selectedContractId],
  );

  const filterSystemVariables = useCallback(variables => {
    const systemVariableNames = VARIABLES.map(variable =>
      variable.name.replace(/\[|\]/g, ''),
    );

    return variables.filter(variable => {
      const name = typeof variable === 'string' ? variable : variable.name;
      return !systemVariableNames.includes(name);
    });
  }, []);

  useEffect(() => {
    if (selectedContract && selectedContract.content) {
      const extractedVars = extractCustomVariablesWithTypes(selectedContract.content);
      setCustomVariables(filterSystemVariables(extractedVars));
    } else {
      setCustomVariables([]);
    }
  }, [selectedContract, filterSystemVariables]);

  const handleSaveContract = useCallback(
    values => {
      setIsLoading(true);

      const content = editorRef.current.getContent();

      const formattedValues = {
        ...values,
        content,
      };

      const selectedContract = contracts.find(
        contract => String(contract._id) === selectedContractId,
      );

      const errorCallback = () => {
        setIsLoading(false);
      };

      if (selectedContract) {
        onUpdateContract(
          selectedContract._id,
          formattedValues,
          () => {
            setIsLoading(false);
            const simpleVars = extractCustomVariables(content);
            console.log('Simple variables:', simpleVars);

            const extractedVars = extractCustomVariablesWithTypes(content);
            setCustomVariables(filterSystemVariables(extractedVars));
          },
          errorCallback,
        );
      }
    },
    [contracts, selectedContractId, onUpdateContract, filterSystemVariables],
  );

  const handleCreateDefaultContract = useCallback(() => {
    setIsLoading(true);

    const errorCallback = () => {
      setIsLoading(false);
    };

    const contractsArray = contracts || [];
    const defaultContract = {
      name: `Meu Contrato #${contractsArray.length + 1}`,
      content: '<p>Digite o conteúdo do contrato aqui...</p>',
    };

    onAddContract(
      defaultContract,
      createdContract => {
        setIsLoading(false);
        if (createdContract && createdContract._id) {
          setSelectedContractId(createdContract._id);

          if (editorRef.current) {
            editorRef.current.setContent(defaultContract.content);
          }
        } else {
          console.error('Contract created but no ID was returned');
          errorCallback();
        }
      },
      errorCallback,
    );
  }, [contracts, onAddContract]);

  useEffect(() => {
    let timeoutId;

    if (isLoading) {
      timeoutId = setTimeout(() => {
        setIsLoading(false);
      }, 10000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isLoading]);

  useEffect(() => {
    onFetchAllContracts(foundContracts => {
      const [firstContract] = foundContracts || [];

      if (firstContract) {
        setSelectedContractId(firstContract._id);
      }

      setIsLoading(false);
    });
  }, [onFetchAllContracts, activeCompany]);

  const handleDuplicate = useCallback(() => {
    setIsLoading(true);

    if (!selectedContract) {
      return;
    }

    const newContract = {
      ...selectedContract,
      _id: null,
    };

    onAddContract(newContract, () => {
      setIsLoading(false);
    });
  }, [selectedContract, onAddContract]);

  const handleDelete = useCallback(() => {
    if (!selectedContract) {
      return;
    }

    const contractsArray = contracts || [];
    const newList = contractsArray.filter(
      contract => contract._id !== selectedContract._id,
    );

    onDeleteContract(selectedContract._id, () => {
      const lastContract = !isEmpty(newList) ? newList[newList.length - 1] : null;

      if (lastContract) {
        setSelectedContractId(lastContract._id);
      } else {
        setSelectedContractId(null);
      }

      setIsLoading(false);
    });
  }, [onDeleteContract, contracts, selectedContract]);

  const handleAddVariable = useCallback(variable => {
    editorRef.current.execCommand('mceInsertContent', false, variable.name);
  }, []);

  const handleAddCustomVariableWithType = useCallback(
    variableName => {
      const formattedVariable = `[${variableName}]`;
      editorRef.current.execCommand('mceInsertContent', false, formattedVariable);

      if (editorRef.current) {
        const content = editorRef.current.getContent();
        const extractedVars = extractCustomVariablesWithTypes(content);
        setCustomVariables(filterSystemVariables(extractedVars));
      }
    },
    [filterSystemVariables],
  );

  const getCursorPosition = useCallback(() => {
    if (!editorRef.current) {
      return {};
    }

    const rng = editorRef.current.selection.getRng();
    const rect = rng.getClientRects()[0];

    if (rect) {
      return {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
      };
    }

    return { top: 0, left: 0 };
  }, []);

  const handleOpenVariables = useCallback(
    e => {
      if (e.key === '@') {
        e.preventDefault();

        const position = getCursorPosition();

        show(e, {
          position: {
            x: position.left + 480,
            y: position.top + 380,
          },
        });
      }
    },
    [show, getCursorPosition],
  );

  const handleEditorChange = useCallback(() => {
    if (editorRef.current) {
      const content = editorRef.current.getContent();
      const extractedVars = extractCustomVariablesWithTypes(content);
      setCustomVariables(filterSystemVariables(extractedVars));
    }
  }, [filterSystemVariables]);

  const CreateNewContractButton = ({ className = '', children = 'Criar novo' }) => (
    <Button
      onClick={handleCreateDefaultContract}
      disabled={isLoading}
      isLoading={isLoading}
      className={`btn btn-sm d-flex justify-content-center align-items-center btn-success ${className}`}
      variant="success-2"
    >
      <FaPlus className="mr-2" />
      {children}
    </Button>
  );

  const ContractsPageHeader = () => (
    <PageHeader
      title="Modelos de Contratos"
      variant="small"
      sideContent={<CreateNewContractButton />}
    />
  );

  if (!isFeatureEnabled('contracts', user.flags)) {
    return null;
  }

  if (isEmpty(contracts) && isLoading) {
    return (
      <Container fluid className="content-wrapper">
        <ContractsPageHeader />
        <Row className="mt-5">
          <Col lg={12}>
            <div className="d-flex flex-column align-items-center justify-content-center">
              <h4 className="text-center">Carregando...</h4>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  if (isEmpty(contracts)) {
    return (
      <Container fluid className="content-wrapper">
        <ContractsPageHeader />
        <Row className="mt-5">
          <Col lg={12}>
            <div className="d-flex flex-column align-items-center justify-content-center">
              <h4 className="text-center">
                Você ainda não possui nenhum modelo de contrato.
              </h4>
              <CreateNewContractButton className="mt-3" />
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="content-wrapper">
      <ContractsPageHeader />
      <Toolbar>
        {contracts.map(contract => {
          const isActive = selectedContractId === contract._id;

          return (
            <ToolbarItem
              key={contract._id}
              role="button"
              onClick={() => setSelectedContractId(contract._id)}
              isActive={isActive}
            >
              <span>{contract.name}</span>
            </ToolbarItem>
          );
        })}
      </Toolbar>
      <Formik
        enableReinitialize
        initialValues={{
          name: selectedContractId
            ? contracts.find(contract => String(contract._id) === selectedContractId)
                ?.name || ''
            : '',
          type: selectedContractId
            ? contracts.find(contract => String(contract._id) === selectedContractId)
                ?.type || ''
            : '',
        }}
        onSubmit={handleSaveContract}
        validationSchema={ValidationSchema}
      >
        {({ handleSubmit, setFieldValue }) => (
          <Row>
            <Col lg={8}>
              <div>
                <Form onSubmit={handleSubmit}>
                  <Row className="mt-3">
                    <Form.Group as={Col}>
                      <Form.Label>Nome do Contrato</Form.Label>
                      <FormTextField name="name" placeholder="Dê um nome ao contrato" />
                    </Form.Group>
                  </Row>
                  <Row className="mt-3">
                    <Form.Group as={Col}>
                      <div className="d-flex align-items-center justify-content-between">
                        <Form.Label>Conteúdo (Corpo do contrato)</Form.Label> <br />
                      </div>
                      <Editor
                        tinymceScriptSrc={EDITOR_SCRIPT_PATH}
                        onInit={(_evt, editor) => (editorRef.current = editor)}
                        initialValue={selectedContract ? selectedContract.content : ''}
                        init={{
                          height: 700,
                          menubar: false,
                          toolbar_mode: 'wrap',
                          selector: 'textarea',
                          plugins:
                            'lists advlist table pagebreak searchreplace template fullscreen',
                          toolbar:
                            'template blocks fontsizeinput bold italic underline alignleft aligncenter alignright alignjustify lineheight numlist bullist table | searchreplace pagebreak | fullscreen print',
                          content_style:
                            'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                          language_url: EDITOR_TRANSLATION_PATH,
                          language: 'pt_BR',
                          browser_spellcheck: false,
                          paste_data_images: false,
                          templates: TEMPLATES,
                        }}
                        onClick={() => {
                          hideAll();
                        }}
                        onChange={handleEditorChange}
                        onKeyDown={handleOpenVariables}
                      />
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex">
                          <Button
                            variant="outline-danger"
                            className="d-flex justify-content-center align-items-center btn btn-sm mr-3"
                            onClick={handleDelete}
                            disabled={isLoading}
                          >
                            <FaTrash className="mr-2" />
                            Excluir
                          </Button>
                          <Button
                            variant="outline-primary"
                            className="d-flex justify-content-center align-items-center btn btn-sm"
                            onClick={handleDuplicate}
                            disabled={isLoading}
                          >
                            <FaCopy className="mr-2" />
                            Duplicar
                          </Button>
                        </div>
                        <Button
                          isLoading={isLoading}
                          disabled={isLoading}
                          onClick={handleSubmit}
                          variant="success"
                          className="d-flex justify-content-center align-items-center"
                          id="btn-save-contract"
                        >
                          <FaSave className="mr-2" />
                          Salvar alterações
                        </Button>
                      </div>
                    </Form.Group>
                  </Row>
                </Form>
              </div>
            </Col>
            <Col lg={4}>
              <div
                style={{
                  position: 'sticky',
                  top: isMobile ? '182px' : '80px',
                  maxHeight: isMobile ? 'calc(100vh - 200px)' : 'calc(100vh - 120px)',
                  overflow: 'auto',
                  paddingRight: '10px',
                  marginBottom: isMobile ? '20px' : '50px',
                }}
                className="variables-sidebar"
              >
                <h4 className="mb-3">Variáveis disponíveis</h4>
                <div className="pr-1">
                  <VariablesList
                    onAddVariable={handleAddVariable}
                    onAddCustomVariableWithType={handleAddCustomVariableWithType}
                    customVariables={customVariables}
                  />
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Formik>
    </Container>
  );
}

Contracts.defaultProps = {
  contracts: [],
};

Contracts.propTypes = {
  activeCompany: PropTypes.object.isRequired,
  contracts: PropTypes.array,
  onFetchContract: PropTypes.func.isRequired,
  onFetchAllContracts: PropTypes.func.isRequired,
  onAddContract: PropTypes.func.isRequired,
  onUpdateContract: PropTypes.func.isRequired,
  onDeleteContract: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
};

export default Contracts;
